import { fetchWithToken } from '@utils/api';
import { API_URL } from '@data/constants';

import type { Registration } from '@services/registrations';

export interface Evaluation {
  id: number;
  registration: Registration;
  name: string;
  givenBy: {
    id: number;
    emailAddress: string;
  } | null;
  createdAt: string | null;
}

export interface FeedbackForm {
  registrationId: string;
  name: string;
}

export const getEvaluations = async ({
  limit = undefined,
  offset = undefined,
  ordering = undefined,
  userId = undefined,
}: {
  limit?: number;
  offset?: number;
  ordering?: string;
  userId?: number;
} = {}): Promise<{ count: number; results: Evaluation[] }> => {
  const queryParams = new URLSearchParams();

  if (limit) {
    queryParams.append('limit', limit.toString());
  }

  if (offset) {
    queryParams.append('offset', offset.toString());
  }

  if (ordering) {
    queryParams.append('ordering', ordering);
  }

  if (userId) {
    queryParams.append('userId', userId.toString());
  }

  const response = await fetchWithToken(
    `${API_URL}/api/evaluations?${queryParams.toString()}`
  );

  if (!response.ok) {
    throw new Error('Error getting evaluations');
  }

  const { data } = (await response.json()) as {
    data: { count: number; results: Evaluation[] };
  };

  return data;
};

export const createEvaluation = async ({
  registrationId,
  name,
}: FeedbackForm): Promise<Evaluation> => {
  const response = await fetchWithToken(`${API_URL}/api/evaluations`, {
    method: 'POST',
    body: JSON.stringify({ registrationId, name }),
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    console.error(response);
    throw new Error('Error creating evaluation');
  }

  const { data } = (await response.json()) as { data: Evaluation };

  return data;
};

export async function updateEvaluation(
  evaluationId: number,
  { registrationId, name }: FeedbackForm
): Promise<Evaluation> {
  const response = await fetchWithToken(
    `${API_URL}/api/evaluations/${evaluationId}`,
    {
      method: 'PATCH',
      body: JSON.stringify({
        registrationId,
        name,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw new Error('Error updating evaluation');
  }

  const { data } = (await response.json()) as { data: Evaluation };

  return data;
}

export async function deleteEvaluation(evaluationId: number): Promise<void> {
  const response = await fetchWithToken(
    `${API_URL}/api/evaluations/${evaluationId}`,
    {
      method: 'DELETE',
    }
  );

  if (!response.ok) {
    throw new Error('Error deleting evaluation');
  }
}
